import { memo } from 'react';
import { Skeleton } from 'antd';
import { useWidgetData, aggregate, accentMap, currencyFmt } from './shared';
import { I } from '../yeni-design/icons';
import type { DashboardWidget, KpiWidgetConfig } from '../../types/panelConfig';

interface Props {
  widget: Extract<DashboardWidget, { type: 'kpi' }>;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

function fmt(n: number, format: 'currency' | 'number' | undefined): string {
  return format === 'number' ? numberFmt.format(n) : currencyFmt.format(n);
}

// Resolve a primary/secondary metric (plain field aggregate OR ratio) to a value.
function metricValue(
  rows: Record<string, unknown>[],
  m: { statField?: string; aggregation?: KpiWidgetConfig['aggregation']; ratio?: { numField: string; denField: string } },
): number | null {
  if (rows.length === 0) return null;
  if (m.ratio) {
    const den = aggregate(rows, m.ratio.denField, 'sum');
    return den ? aggregate(rows, m.ratio.numField, 'sum') / den : null;
  }
  if (!m.statField) return null;
  return aggregate(rows, m.statField, m.aggregation ?? 'sum');
}

function KpiWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `kpi-${widget.reportCode}`, parameters);
  const { label, icon, accent, footer, secondary, format } = widget.config;
  const rows = data ?? [];

  const primary = metricValue(rows, widget.config);
  const value = primary === null ? '—' : fmt(primary, format);

  const accentColor = accentMap[accent || 'blue'] ?? accentMap.blue;
  const IconCmp = (icon && I[icon as keyof typeof I]) || I.Profit;

  // Secondary metric line (e.g. Fiş card → "Sepet ort. ₺224"), else static footer text.
  let footerText: string | null = footer ?? null;
  if (secondary) {
    const sv = metricValue(rows, secondary);
    footerText = `${secondary.label} ${sv === null ? '—' : fmt(sv, secondary.format)}`;
  }

  return (
    <div
      data-testid="kpi-widget"
      style={{
        background: 'var(--color-card-bg)',
        border: '1px solid var(--color-border)',
        borderTop: `2px solid ${accentColor}`,
        borderRadius: 6,
        padding: '16px 16px 14px',
        minHeight: 108,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            display: 'grid',
            placeItems: 'center',
            color: accentColor,
            background: `color-mix(in srgb, ${accentColor} 12%, transparent)`,
            flexShrink: 0,
          }}
        >
          <IconCmp size={17} />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {label}
        </span>
      </div>

      {isLoading ? (
        <Skeleton.Button active size="small" style={{ width: 130, height: 28 }} />
      ) : (
        <span
          className="serif tnum"
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
          }}
        >
          {value}
        </span>
      )}

      {!isLoading && footerText && (
        <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 'auto' }}>
          {footerText}
        </span>
      )}
    </div>
  );
}

export const KpiWidget = memo(KpiWidgetImpl);
